import React, { useMemo } from 'react'
import { Typography, Grid, Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import TableTemplate from '../../Common/Table/TableTemplate'
import { setDateFormat, setTimeFormat } from '../../../utils/dateUtils'
import EventBadges from '../../Events/EventBadges'
import { fontSizes, fontWeights } from '../../Common/Typography/TypographyStyles'
import { Event } from '../../../redux/event/manageEvents/types'
import { useAppSelector } from '../../../redux/store'
import {
  selectEventsError,
  selectEventsLoading,
  selectFilteredEventsIsNotArchived,
} from '../../../redux/event/manageEvents/selector'

interface Column {
  field: string
  headerName: string
  headerAlign?: 'left' | 'right' | 'center'
  bodyAlignment?: 'center' | 'right' | 'left'
  width?: string | number
  renderCell?: (value: any, item: any) => React.ReactNode
  renderFormat?: (value: any) => React.ReactNode
  sortable: boolean
}

const EventTable: React.FC = () => {
  const navigate = useNavigate()
  const eventData = useAppSelector(selectFilteredEventsIsNotArchived)
  const loading = useAppSelector(selectEventsLoading)
  const error = useAppSelector(selectEventsError)

  const columns = useMemo<Column[]>(
    () => [
      {
        field: 'id',
        headerName: 'EVENT ID',
        bodyAlignment: 'center',
        sortable: true,
      },
      {
        field: 'title',
        headerName: 'TITLE',
        bodyAlignment: 'center',
        sortable: true,
        renderCell: (param: string) => (
          <Typography
            sx={{
              maxWidth: 200,
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'left',
              display: 'block',
              margin: '0 auto',
              fontSize: fontSizes.size12,
              fontWeight: fontWeights[700],
            }}
          >
            {param}
          </Typography>
        ),
      },
      {
        field: 'startDate',
        headerName: 'START DATE',
        bodyAlignment: 'center',
        sortable: true,
        renderFormat: (value: string | Date | null) => (
          <Grid container spacing={0} direction='column' alignContent='center'>
            <Grid>
              <Typography
                sx={{
                  fontSize: fontSizes.size12,
                  fontWeight: fontWeights[700],
                  lineHeight: 'ratio9to8',
                  letterSpacing: 'loose',
                }}
              >
                {value ? setDateFormat(value) : 'N/A'}
              </Typography>
            </Grid>
            <Grid>
              <Typography
                sx={{
                  fontSize: fontSizes.size10,
                  fontWeight: fontWeights[400],
                  lineHeight: 'ratio9to8',
                  letterSpacing: 'loose',
                  color: 'text.secondary',
                }}
              >
                {value ? setTimeFormat(value) : ''}
              </Typography>
            </Grid>
          </Grid>
        ),
      },
      {
        field: 'endDate',
        headerName: 'END DATE',
        bodyAlignment: 'center',
        sortable: true,
        renderFormat: (value: string | Date | null) => (
          <Grid container spacing={0} direction='column' alignContent='center'>
            <Grid>
              <Typography
                sx={{
                  fontSize: fontSizes.size12,
                  fontWeight: fontWeights[700],
                  lineHeight: 'ratio9to8',
                  letterSpacing: 'loose',
                }}
              >
                {value ? setDateFormat(value) : 'N/A'}
              </Typography>
            </Grid>
            <Grid>
              <Typography
                sx={{
                  fontSize: fontSizes.size10,
                  fontWeight: fontWeights[400],
                  lineHeight: 'ratio9to8',
                  letterSpacing: 'loose',
                  color: 'text.secondary',
                }}
              >
                {value ? setTimeFormat(value) : ''}
              </Typography>
            </Grid>
          </Grid>
        ),
      },
      {
        field: 'category',
        headerName: 'CATEGORY',
        bodyAlignment: 'center',
        sortable: false,
        renderFormat: (value: string) => (
          <Grid container justifyContent='center' alignItems='center'>
            <EventBadges value={value} badgeType='categoryBadge' />
          </Grid>
        ),
      },
      {
        field: 'id',
        headerName: 'ACTION',
        bodyAlignment: 'center',
        sortable: false,
        renderCell: (_id: number, row: Event) => (
          <Button
            variant='contained'
            color='primary'
            size='small'
            onClick={() => navigate(`/reports/event/${row._id}`)}
          >
            View Report
          </Button>
        ),
      },
    ],
    [],
  )

  return (
    <React.Fragment>
      <TableTemplate columns={columns} datas={eventData} state={{ loading, error }} uniqueField='id' />
    </React.Fragment>
  )
}

export default EventTable
